import { type PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FaComments, FaTimes, FaPlus, FaExpand } from 'react-icons/fa';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { showToast } from '@/components/Layout/Toast';
import Dropdown from '@/components/UI/Dropdown';
import { useAuth } from '@context/AuthContext';
import { ChatWindow } from './ChatWindow';

const CHAT_WIDGET_WIDTH_STORAGE_KEY = 'agentChatWidgetWidthPx';
const CHAT_WIDGET_HEIGHT_STORAGE_KEY = 'agentChatWidgetHeightPx';
const CHAT_WIDGET_USER_SIZED_STORAGE_KEY = 'agentChatWidgetUserSized';
const CHAT_WIDGET_BASE_WIDTH_PX = 420;
const CHAT_WIDGET_BASE_HEIGHT_PX = 560;
const CHAT_WIDGET_DEFAULT_SCALE = 2;
const CHAT_WIDGET_MIN_WIDTH_PX = 340;
const CHAT_WIDGET_MIN_HEIGHT_PX = 440;
const CHAT_WIDGET_MAX_WIDTH_MARGIN_PX = 32; // matches w-[calc(100vw-2rem)]
const CHAT_WIDGET_MAX_HEIGHT_MARGIN_PX = 96; // matches h-[calc(100vh-6rem)]

function parseStoredPx(value: string | null): number | null {
    if (!value) return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    const floored = Math.floor(parsed);
    return floored > 0 ? floored : null;
}

function clampPx(value: number, min: number, max: number): number {
    if (!Number.isFinite(value)) return min;
    if (value < min) return min;
    if (value > max) return max;
    return value;
}

function clampWidgetSize(params: { widthPx: number; heightPx: number }): { widthPx: number; heightPx: number } {
    if (typeof window === 'undefined') {
        return {
            widthPx: Math.max(CHAT_WIDGET_MIN_WIDTH_PX, Math.floor(params.widthPx)),
            heightPx: Math.max(CHAT_WIDGET_MIN_HEIGHT_PX, Math.floor(params.heightPx))
        };
    }

    const maxWidthPx = Math.max(CHAT_WIDGET_MIN_WIDTH_PX, Math.floor(window.innerWidth - CHAT_WIDGET_MAX_WIDTH_MARGIN_PX));
    const maxHeightPx = Math.max(CHAT_WIDGET_MIN_HEIGHT_PX, Math.floor(window.innerHeight - CHAT_WIDGET_MAX_HEIGHT_MARGIN_PX));

    return {
        widthPx: clampPx(Math.floor(params.widthPx), CHAT_WIDGET_MIN_WIDTH_PX, maxWidthPx),
        heightPx: clampPx(Math.floor(params.heightPx), CHAT_WIDGET_MIN_HEIGHT_PX, maxHeightPx)
    };
}

function getRecommendedWidgetSize(): { widthPx: number; heightPx: number } {
    return clampWidgetSize({
        widthPx: CHAT_WIDGET_BASE_WIDTH_PX * CHAT_WIDGET_DEFAULT_SCALE,
        heightPx: CHAT_WIDGET_BASE_HEIGHT_PX * CHAT_WIDGET_DEFAULT_SCALE
    });
}

export function ChatWidget() {
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isAuthenticated, isLoading } = useAuth();
    const recommendedSize = useMemo(() => getRecommendedWidgetSize(), []);
    const [isOpen, setIsOpen] = useState(false);
    const [sessionId, setSessionId] = useState<number | null>(null);
    const [isCreatingNewSession, setIsCreatingNewSession] = useState(false);
    const [widgetWidthPx, setWidgetWidthPx] = useState(recommendedSize.widthPx);
    const [widgetHeightPx, setWidgetHeightPx] = useState(recommendedSize.heightPx);
    const widgetRef = useRef<HTMLDivElement | null>(null);
    const widgetSizeRef = useRef({ widthPx: recommendedSize.widthPx, heightPx: recommendedSize.heightPx });

    const [isSmUp, setIsSmUp] = useState(() => {
        if (typeof window === 'undefined') return true;
        return window.matchMedia('(min-width: 640px)').matches;
    });

    const resizeRef = useRef<{
        mode: 'left' | 'top' | 'topLeft';
        startX: number;
        startY: number;
        startWidthPx: number;
        startHeightPx: number;
    } | null>(null);

    const sessionsQuery = useQuery({
        ...trpc.agent.listSessionsEnriched.queryOptions({ limit: 10 }),
        enabled: isAuthenticated && isOpen && !isLoading,
        retry: false
    });

    const recentSessions = useMemo(() => sessionsQuery.data?.sessions ?? [], [sessionsQuery.data?.sessions]);

    const sessionTitle = useMemo(() => {
        if (!sessionId) return null;
        const match = recentSessions.find((s) => s.id === sessionId);
        return match?.title ?? null;
    }, [recentSessions, sessionId]);

    const createSessionMutation = useTrpcMutation(trpc.agent.createSession.mutationOptions(), {
        successMessage: 'New chat created',
        fallbackErrorMessage: 'Failed to create new chat'
    });

    const openWidget = useCallback(() => {
        const hasUserSized = localStorage.getItem(CHAT_WIDGET_USER_SIZED_STORAGE_KEY) === 'true';
        const storedWidth = hasUserSized ? parseStoredPx(localStorage.getItem(CHAT_WIDGET_WIDTH_STORAGE_KEY)) : null;
        const storedHeight = hasUserSized ? parseStoredPx(localStorage.getItem(CHAT_WIDGET_HEIGHT_STORAGE_KEY)) : null;

        const desired = storedWidth && storedHeight ? { widthPx: storedWidth, heightPx: storedHeight } : getRecommendedWidgetSize();
        const clamped = clampWidgetSize(desired);
        setWidgetWidthPx(clamped.widthPx);
        setWidgetHeightPx(clamped.heightPx);
        setIsOpen(true);
    }, []);

    useEffect(() => {
        widgetSizeRef.current = { widthPx: widgetWidthPx, heightPx: widgetHeightPx };
    }, [widgetHeightPx, widgetWidthPx]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia('(min-width: 640px)');
        const onChange = (e: MediaQueryListEvent) => setIsSmUp(e.matches);

        mq.addEventListener('change', onChange);
        setIsSmUp(mq.matches);

        return () => {
            mq.removeEventListener('change', onChange);
        };
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        if (!isSmUp) return;

        const onPointerMove = (e: PointerEvent) => {
            const { current } = resizeRef;
            if (!current) return;

            const dx = current.startX - e.clientX;
            const dy = current.startY - e.clientY;

            let nextWidthPx = current.startWidthPx;
            let nextHeightPx = current.startHeightPx;

            if (current.mode === 'left' || current.mode === 'topLeft') nextWidthPx = current.startWidthPx + dx;
            if (current.mode === 'top' || current.mode === 'topLeft') nextHeightPx = current.startHeightPx + dy;

            const clamped = clampWidgetSize({ widthPx: nextWidthPx, heightPx: nextHeightPx });
            setWidgetWidthPx(clamped.widthPx);
            setWidgetHeightPx(clamped.heightPx);
        };

        const stopResize = () => {
            if (!resizeRef.current) return;
            resizeRef.current = null;

            const { current } = widgetSizeRef;
            localStorage.setItem(CHAT_WIDGET_WIDTH_STORAGE_KEY, String(current.widthPx));
            localStorage.setItem(CHAT_WIDGET_HEIGHT_STORAGE_KEY, String(current.heightPx));
            localStorage.setItem(CHAT_WIDGET_USER_SIZED_STORAGE_KEY, 'true');

            document.body.style.userSelect = '';
        };

        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', stopResize);
        window.addEventListener('pointercancel', stopResize);

        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', stopResize);
            window.removeEventListener('pointercancel', stopResize);
        };
    }, [isOpen, isSmUp]);

    const beginResize = useCallback(
        (mode: 'left' | 'top' | 'topLeft') => (e: ReactPointerEvent<HTMLDivElement>) => {
            if (!isSmUp) return;
            if (e.button !== 0) return;
            e.preventDefault();
            e.stopPropagation();

            resizeRef.current = {
                mode,
                startX: e.clientX,
                startY: e.clientY,
                startWidthPx: widgetSizeRef.current.widthPx,
                startHeightPx: widgetSizeRef.current.heightPx
            };

            document.body.style.userSelect = 'none';
        },
        [isSmUp]
    );

    const close = useCallback(() => setIsOpen(false), []);

    const createNewChat = useCallback(async () => {
        const previousSessionId = sessionId;
        setIsCreatingNewSession(true);
        setSessionId(null);

        try {
            const res = await createSessionMutation.mutateAsync({});
            if (!res.success) throw new Error(res.errorMessages?.[0] ?? 'Failed to create new chat');
            setSessionId(res.sessionId);
            await queryClient.invalidateQueries(trpc.agent.listSessionsEnriched.pathFilter());
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to create new chat';
            showToast(message, 'error');
            setSessionId(previousSessionId);
        } finally {
            setIsCreatingNewSession(false);
        }
    }, [createSessionMutation, sessionId]);

    const openFullChat = useCallback(() => {
        if (sessionId) {
            void navigate(`/user/chat/${sessionId}`);
        } else {
            void navigate('/user/chat/new');
        }
    }, [navigate, sessionId]);

    if (!isAuthenticated || isLoading) return null;
    if (location.pathname.startsWith('/user/chat')) return null;

    return (
        <>
            {!isOpen && (
                <button
                    aria-label="Open AI chat"
                    className="fixed bottom-4 right-4 z-[9997] rounded-full bg-blue-600 hover:bg-blue-500 text-white shadow-lg w-12 h-12 flex items-center justify-center"
                    type="button"
                    onClick={openWidget}
                >
                    <FaComments />
                </button>
            )}

            {isOpen && (
                <div className="fixed inset-0 z-[9998]">
                    <div className="absolute inset-0 bg-black/40" role="presentation" onClick={close} />
                    <div
                        className={[
                            'absolute bottom-4 right-4',
                            'w-[calc(100vw-2rem)] h-[calc(100vh-6rem)]',
                            'sm:min-w-[340px] sm:min-h-[440px]',
                            'sm:max-w-[calc(100vw-2rem)] sm:max-h-[calc(100vh-6rem)]',
                            'bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl flex flex-col overflow-hidden',
                            'resize-none'
                        ].join(' ')}
                        ref={widgetRef}
                        style={isSmUp ? { width: widgetWidthPx, height: widgetHeightPx } : undefined}
                    >
                        {isSmUp && (
                            <>
                                <div
                                    aria-label="Resize chat"
                                    className="absolute left-0 top-0 z-10 h-5 w-5 cursor-nwse-resize"
                                    role="button"
                                    tabIndex={-1}
                                    title="Resize"
                                    onPointerDown={beginResize('topLeft')}
                                >
                                    <div className="absolute left-[3px] top-[3px] h-[14px] w-[14px] border-l border-t border-zinc-600/70" />
                                    <div className="absolute left-[6px] top-[6px] h-[10px] w-[10px] border-l border-t border-zinc-600/50" />
                                </div>
                                <div
                                    aria-label="Resize chat width"
                                    className="absolute left-0 top-0 z-10 h-full w-2 cursor-ew-resize"
                                    role="button"
                                    tabIndex={-1}
                                    title="Resize width"
                                    onPointerDown={beginResize('left')}
                                />
                                <div
                                    aria-label="Resize chat height"
                                    className="absolute left-0 top-0 z-10 h-2 w-full cursor-ns-resize"
                                    role="button"
                                    tabIndex={-1}
                                    title="Resize height"
                                    onPointerDown={beginResize('top')}
                                />
                            </>
                        )}
                        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                <Dropdown
                                    buttonClassName="!px-2 !py-1 text-xs"
                                    buttonContent={
                                        <span className="text-sm font-semibold text-white truncate max-w-[200px]">
                                            {sessionTitle || (sessionId ? `Chat #${sessionId}` : 'New Chat')}
                                        </span>
                                    }
                                    menuClassName="!w-64"
                                >
                                    <button
                                        className="w-full text-left px-3 py-1.5 text-sm text-blue-400 hover:bg-zinc-700 font-medium"
                                        disabled={isCreatingNewSession || createSessionMutation.isPending}
                                        type="button"
                                        onClick={() => {
                                            void createNewChat();
                                        }}
                                    >
                                        + New Chat
                                    </button>
                                    {recentSessions.map((s) => (
                                        <button
                                            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-zinc-700 truncate ${s.id === sessionId ? 'text-blue-400' : 'text-zinc-300'}`}
                                            key={s.id}
                                            type="button"
                                            onClick={() => setSessionId(s.id)}
                                        >
                                            {(s.title ?? '').trim() || `Chat #${s.id}`}
                                        </button>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    className="text-xs text-zinc-300 hover:text-white flex items-center gap-1"
                                    disabled={isCreatingNewSession || createSessionMutation.isPending}
                                    type="button"
                                    onClick={() => {
                                        void createNewChat();
                                    }}
                                >
                                    <FaPlus />
                                </button>
                                <button
                                    className="text-xs text-zinc-300 hover:text-white flex items-center gap-1"
                                    type="button"
                                    onClick={openFullChat}
                                >
                                    <FaExpand />
                                </button>
                                <button aria-label="Close chat" className="text-zinc-300 hover:text-white" type="button" onClick={close}>
                                    <FaTimes />
                                </button>
                            </div>
                        </div>
                        <ChatWindow autoFocus sessionId={sessionId} setSessionId={setSessionId} />
                    </div>
                </div>
            )}
        </>
    );
}
